import { useLang } from '../contexts/LanguageContext';
import { ArrowLeft, Shield, Lock, Eye, Database, Trash2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="privacy-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10">
        <Link to="/" className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#FADB5F] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0A3A82]/20 flex items-center justify-center">
            <Shield className="w-6 h-6 text-[#FADB5F]" />
          </div>
          <h1 className="text-3xl font-bold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Privacy Policy
          </h1>
        </div>

        <div className="space-y-8 text-sm text-[#94A3B8] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <p className="text-xs text-[#475569]">Last updated: April 2026</p>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Information We Collect</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li><strong className="text-[#F1F5F9]">Account Data:</strong> Name, email address, phone number (optional), country (optional) when you register.</li>
              <li><strong className="text-[#F1F5F9]">Listing Data:</strong> Vehicle details, descriptions, images, pricing, and location you provide when posting ads.</li>
              <li><strong className="text-[#F1F5F9]">Messages:</strong> Communications between buyers and sellers through our platform.</li>
              <li><strong className="text-[#F1F5F9]">Payment Data:</strong> Processed securely by Stripe. We never store your credit card details.</li>
              <li><strong className="text-[#F1F5F9]">Technical Data:</strong> IP address, browser type, and language preferences for security and functionality.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>2. How We Use Your Data</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>To provide and maintain the EuroMoto marketplace platform.</li>
              <li>To authenticate your identity and secure your account.</li>
              <li>To process premium listing payments via Stripe.</li>
              <li>To enable communication between buyers and sellers.</li>
              <li>To prevent fraud, abuse, and brute-force attacks.</li>
              <li>To comply with legal obligations under EU law.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Lock className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>3. Data Security</h2>
            </div>
            <ul className="space-y-2 ml-7 list-disc">
              <li>Passwords are hashed using bcrypt encryption.</li>
              <li>Sessions are managed with secure httpOnly cookies that cannot be accessed by JavaScript.</li>
              <li>All payment processing is handled by Stripe, which is PCI DSS Level 1 certified.</li>
              <li>Brute-force protection limits login attempts (5 attempts, 15-minute lockout).</li>
              <li>All data transmission is encrypted via HTTPS/TLS.</li>
            </ul>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Cookies</h2>
            </div>
            <p>EuroMoto uses only <strong className="text-[#F1F5F9]">essential cookies</strong>:</p>
            <ul className="space-y-2 ml-7 list-disc mt-2">
              <li><strong className="text-[#F1F5F9]">access_token / refresh_token:</strong> Secure httpOnly cookies for authentication. Expire after 24h / 7 days.</li>
              <li><strong className="text-[#F1F5F9]">euromoto_lang:</strong> Stores your language preference (localStorage).</li>
              <li><strong className="text-[#F1F5F9]">euromoto_cookie_consent:</strong> Remembers your cookie choice (localStorage).</li>
            </ul>
            <p className="mt-3">We do <strong className="text-[#F1F5F9]">not</strong> use tracking, analytics, or advertising cookies.</p>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Trash2 className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Your Rights (GDPR)</h2>
            </div>
            <p>Under EU GDPR, you have the right to:</p>
            <ul className="space-y-2 ml-7 list-disc mt-2">
              <li><strong className="text-[#F1F5F9]">Access</strong> your personal data.</li>
              <li><strong className="text-[#F1F5F9]">Rectify</strong> inaccurate information.</li>
              <li><strong className="text-[#F1F5F9]">Delete</strong> your account and all associated data.</li>
              <li><strong className="text-[#F1F5F9]">Export</strong> your data in a portable format.</li>
              <li><strong className="text-[#F1F5F9]">Object</strong> to data processing.</li>
            </ul>
            <p className="mt-3">Contact us at <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a> to exercise your rights.</p>
          </section>

          <section className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-[#FADB5F]" />
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Contact</h2>
            </div>
            <p>For privacy-related inquiries:</p>
            <p className="mt-2">Email: <a href="mailto:Marketplacekoko@gmail.com" className="text-[#FADB5F] hover:underline">Marketplacekoko@gmail.com</a></p>
            <p>EuroMoto — European Vehicle Marketplace</p>
          </section>
        </div>
      </div>
    </div>
  );
}
